import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  Alert,
  Table,
  Spinner
} from 'reactstrap';
import { useAuth } from '../../../../contexts/AuthContext';
import CommunicationService from '../../../../service/CommunicationService.ts';

const EmailsAction = ({ isOpen, toggle, deal }) => {
  const { getToken } = useAuth();
  const [emailHistory, setEmailHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    if (isOpen && deal?.privateId) {
      loadEmailHistory();
    }
  }, [isOpen, deal]);

  const loadEmailHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = getToken();
      
      if (!deal?.privateId) {
        setError('Deal ID not found');
        return;
      }

      const response = await CommunicationService.getHistory(token, deal.privateId);
      
      // Filter only EMAIL communications
      const emails = Array.isArray(response.data) 
        ? response.data.filter(comm => comm.recipientEmail)
        : [];
      
      // Sort by sentAt date, most recent first
      const sortedEmails = emails.sort((a, b) => {
        const dateA = new Date(a.sentAt);
        const dateB = new Date(b.sentAt);
        return dateB - dateA; // Descending order (most recent first)
      });
      
      setEmailHistory(sortedEmails);
    } catch (err) {
      console.error('Error loading email history:', err);
      setError('Error loading email history: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${month}/${day}/${year} ${hours}:${minutes}`;
    } catch (error) {
      return 'N/A';
    }
  };

  const truncateText = (text, maxLength) => {
    if (!text) return 'N/A';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const handleEmailClick = (email) => {
    setSelectedEmail(email);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedEmail(null);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={toggle}
        size="lg"
      >
        <ModalHeader toggle={toggle}>
          <i className="ni ni-email-83 mr-2" />
          Email History
        </ModalHeader>
        <ModalBody style={{ padding: '1.5rem' }}>
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="mt-2">Loading email history...</p>
            </div>
          ) : error ? (
            <Alert color="danger">
              <i className="fas fa-exclamation-triangle mr-2" />
              {error}
            </Alert>
          ) : emailHistory.length === 0 ? (
            <div className="text-center py-5">
              <i className="ni ni-email-83 text-muted mb-3" style={{ fontSize: '3rem' }} />
              <Alert color="info" className="mb-0">
                <i className="ni ni-notification-70 mr-2" />
                <strong>No History</strong>
                <p className="mb-0 mt-2">
                  No emails found for this deal.
                </p>
              </Alert>
            </div>
          ) : (
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              <Table className="align-items-center table-flush" hover responsive>
                <thead className="thead-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th scope="col">Subject</th>
                    <th scope="col">Mail</th>
                    <th scope="col">Sent At</th>
                  </tr>
                </thead>
                <tbody>
                  {emailHistory.map((email, index) => (
                    <tr 
                      key={index}
                      onClick={() => handleEmailClick(email)}
                      style={{ cursor: 'pointer' }}
                      className="table-row-hover"
                    >
                      <td>
                        <span className="font-weight-bold">
                          {truncateText(email.subject, 32)}
                        </span>
                      </td>
                      <td>
                        <span className="text-muted">
                          {truncateText(email.renderedText, 32)}
                        </span>
                      </td>
                      <td>
                        <span className="text-sm">
                          {formatDate(email.sentAt)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </ModalBody>
      </Modal>

      {/* Detail Modal */}
      <Modal
        isOpen={showDetailModal}
        toggle={handleCloseDetailModal}
        size="lg"
      >
        <ModalHeader toggle={handleCloseDetailModal}>
          <i className="ni ni-email-83 mr-2" />
          {selectedEmail?.subject || 'Email Details'}
        </ModalHeader>
        <ModalBody style={{ padding: '1.5rem' }}>
          {selectedEmail && (
            <>
              <div className="mb-3">
                <div className="row mb-2">
                  <div className="col-md-6">
                    <strong>To:</strong> {selectedEmail.recipientEmail}
                  </div>
                  <div className="col-md-6">
                    <strong>Name:</strong> {selectedEmail.recipientName || 'N/A'}
                  </div>
                </div>
                <div className="row mb-2">
                  <div className="col-md-6">
                    <strong>Sent:</strong> {formatDate(selectedEmail.sentAt)}
                  </div>
                  <div className="col-md-6">
                    <strong>Status:</strong> {selectedEmail.status || 'N/A'}
                  </div>
                </div>
                {selectedEmail.action && (
                  <div className="row mb-2">
                    <div className="col-md-6">
                      <strong>Action:</strong> {selectedEmail.action}
                    </div>
                    <div className="col-md-6">
                      <strong>Template:</strong> {selectedEmail.templateName || 'N/A'}
                    </div>
                  </div>
                )}
              </div>
              <hr />
              <div className="mt-3">
                <strong className="mb-2 d-block">Email Content:</strong>
                <div 
                  style={{ 
                    border: '1px solid #dee2e6', 
                    borderRadius: '0.375rem', 
                    padding: '1rem',
                    backgroundColor: '#f8f9fa',
                    maxHeight: '400px',
                    overflowY: 'auto'
                  }}
                  dangerouslySetInnerHTML={{ __html: selectedEmail.body || '<p>No content available</p>' }}
                />
              </div>
            </>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default EmailsAction;

